import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { JinaIcon, TavilyIcon } from '@/lib/icons';
import { cn } from '@/lib/utils';
import { useReactFlow } from '@xyflow/react';
import {
  PlayIcon,
  RotateCcwIcon,
  SettingsIcon,
  WrenchIcon,
} from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { type ComponentProps, useCallback, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { NodeLayout } from '../layout';
import type { ToolNodeProps } from './index';

interface ToolPrimitiveProps extends ToolNodeProps {
  title: string;
}

// Available tools for demonstration
const availableTools = [
  {
    name: 'web-search',
    label: 'Web Search',
    icon: TavilyIcon,
    description: 'Search the web for information',
  },
  {
    name: 'web-scraping',
    label: 'Web Scraper',
    icon: JinaIcon,
    description: 'Extract content from any webpage using Jina AI Reader',
  },
];

export function ToolTransform({ id, data, title }: ToolPrimitiveProps) {
  const [isConfigExpanded, setIsConfigExpanded] = useState(true);
  const [isExecuting, setIsExecuting] = useState(false);
  const { updateNodeData } = useReactFlow();

  const handleInputChange = (field: keyof typeof data, value: string) => {
    // Actually update the node data
    updateNodeData(id, { [field]: value });
  };

  const handleExecute = useCallback(async () => {
    if (!data.toolName) return;

    // Validate tool-specific requirements
    if (data.toolName === 'web-scraping' && !data.targetUrl) {
      updateNodeData(id, {
        generated: {
          result: undefined,
          status: 'error',
          error: 'Target URL is required for web scraping',
        },
      });
      return;
    }

    if (data.toolName === 'web-search' && !data.searchQuery) {
      updateNodeData(id, {
        generated: {
          result: undefined,
          status: 'error',
          error: 'Search query is required for web search',
        },
      });
      return;
    }

    setIsExecuting(true);
    // Update status to running
    updateNodeData(id, {
      generated: {
        ...data.generated,
        status: 'running',
      },
    });

    try {
      if (data.toolName === 'web-scraping') {
        // Reset the result first
        updateNodeData(id, {
          generated: {
            result: undefined,
            status: 'running',
            error: undefined,
          },
        });
        // Call the web scraping API
        const response = await fetch('/api/tools/web-scraping', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            url: data.targetUrl,
            cssSelector: data.cssSelector,
            removeImages: data.removeImages === 'true',
            gatherLinks: data.gatherLinks === 'true',
          }),
        });

        const result = await response.json();

        if (result.success) {
          const formattedResult = `**Title:** ${result.data.title}\n\n**URL:** ${result.data.url}\n\n**Description:** ${result.data.description}\n\n**Content:**\n${result.data.content}`;
          updateNodeData(id, {
            generated: {
              result: formattedResult,
              status: 'completed',
              error: undefined,
            },
            text: result.data.content, // Set text output for subsequent nodes
            updatedAt: new Date().toISOString(),
          });
        } else {
          updateNodeData(id, {
            generated: {
              result: undefined,
              status: 'error',
              error: result.error || 'Failed to scrape webpage',
            },
          });
        }
      } else if (data.toolName === 'web-search') {
        // Reset the result first
        updateNodeData(id, {
          generated: {
            result: undefined,
            status: 'running',
            error: undefined,
          },
        });

        // Call the web search API
        const response = await fetch('/api/tools/web-search', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            query: data.searchQuery,
            maxResults: 5,
            searchDepth: 'basic',
            includeAnswer: true,
            topic: 'general',
          }),
        });

        const result = await response.json();

        if (result.success) {
          updateNodeData(id, {
            generated: {
              result: result.data.formatted_summary,
              status: 'completed',
              error: undefined,
            },
            text: result.data.answer || result.data.formatted_summary, // Set text output for subsequent nodes
            updatedAt: new Date().toISOString(),
          });
        } else {
          updateNodeData(id, {
            generated: {
              result: undefined,
              status: 'error',
              error: result.error || 'Failed to perform web search',
            },
          });
        }
      } else {
        // Simulate other tool execution
        setTimeout(() => {
          const mockResult = `Tool "${data.toolName}" executed successfully with mock data.`;
          updateNodeData(id, {
            generated: {
              result: mockResult,
              status: 'completed',
              error: undefined,
            },
            text: mockResult, // Set text output for subsequent nodes
            updatedAt: new Date().toISOString(),
          });
        }, 2000);
      }
    } catch (error) {
      updateNodeData(id, {
        generated: {
          result: undefined,
          status: 'error',
          error: error instanceof Error ? error.message : 'An error occurred',
        },
      });
    } finally {
      setIsExecuting(false);
    }
  }, [
    data.toolName,
    data.targetUrl,
    data.cssSelector,
    data.removeImages,
    data.gatherLinks,
    data.generated,
    data.searchQuery,
    updateNodeData,
    id,
  ]);

  const selectedTool = availableTools.find(
    (tool) => tool.name === data.toolName
  );

  const toolbar = useMemo(() => {
    const items: ComponentProps<typeof NodeLayout>['toolbar'] = [];
    items.push({
      tooltip: 'Tool Configuration',
      children: (
        <Button
          size="icon"
          className={cn(
            isConfigExpanded
              ? 'rounded-full bg-cyan-500! hover:bg-cyan-500! dark:bg-cyan-400! dark:hover:bg-cyan-400!'
              : 'rounded-full'
          )}
          variant={isConfigExpanded ? 'default' : 'outline'}
          onClick={() => setIsConfigExpanded(!isConfigExpanded)}
        >
          <SettingsIcon size={12} />
        </Button>
      ),
    });

    if (selectedTool && data.generated?.result) {
      items.push({
        tooltip: 'Rerun Tool',
        children: (
          <Button
            className="rounded-full bg-cyan-500! hover:bg-cyan-500! dark:bg-cyan-600! dark:hover:bg-cyan-500!"
            onClick={handleExecute}
            disabled={
              isExecuting ||
              !data.toolName ||
              (data.toolName === 'web-scraping' && !data.targetUrl) ||
              (data.toolName === 'web-search' && !data.searchQuery)
            }
          >
            <RotateCcwIcon size={12} />
            Rerun Tool
          </Button>
        ),
      });
    } else if (selectedTool) {
      items.push({
        tooltip: 'Execute Tool',
        children: (
          <Button
            size="icon"
            className="rounded-full bg-cyan-500! hover:bg-cyan-500! dark:bg-cyan-600! dark:hover:bg-cyan-500!"
            onClick={handleExecute}
            disabled={
              isExecuting ||
              !data.toolName ||
              (data.toolName === 'web-scraping' && !data.targetUrl) ||
              (data.toolName === 'web-search' && !data.searchQuery)
            }
          >
            <PlayIcon size={12} />
          </Button>
        ),
      });
    }

    return items;
  }, [
    isConfigExpanded,
    selectedTool,
    handleExecute,
    isExecuting,
    data.toolName,
    data.generated?.result,
    data.targetUrl,
    data.searchQuery,
  ]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'text-blue-600 bg-blue-50 border-blue-200';
      case 'completed':
        return 'text-green-600 bg-green-50 border-green-200';
      case 'error':
        return 'text-red-600 bg-red-50 border-red-200';
      default:
        return 'text-neutral-600 bg-neutral-50 border-neutral-200';
    }
  };

  return (
    <NodeLayout
      id={id}
      type="tool"
      title={title}
      data={data}
      toolbar={toolbar}
      className="node-tool"
    >
      <div className="divide-y divide-neutral-200 dark:divide-neutral-800">
        {/* Header */}
        <div className="flex items-center gap-2 p-3">
          <div className="relative flex items-center justify-center rounded-lg bg-cyan-200/60 p-2.5 dark:bg-cyan-800/60">
            <WrenchIcon className="size-4 text-cyan-600 dark:text-cyan-400" />
          </div>
          <Select
            value={data.toolName || ''}
            onValueChange={(value) => handleInputChange('toolName', value)}
          >
            <SelectTrigger className="h-8 w-full text-xs">
              <SelectValue placeholder="Choose a tool..." />
            </SelectTrigger>
            <SelectContent>
              {availableTools.map((tool) => (
                <SelectItem key={tool.name} value={tool.name}>
                  <div className="flex items-center gap-2 overflow-hidden">
                    {tool.icon && (
                      <tool.icon
                        className={cn(
                          'size-4 shrink-0',
                          data.toolName === tool.name &&
                            'text-primary-foreground'
                        )}
                      />
                    )}
                    <span className="block truncate">{tool.label}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Advanced Configuration */}

        {/* Configuration */}
        {isConfigExpanded && (
          <AnimatePresence mode="wait">
            {selectedTool && (
              <motion.div
                key={data.toolName}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
                className="space-y-3 overflow-hidden p-3"
              >
                {/* Example configuration fields based on tool type */}
                {data.toolName === 'web-search' && (
                  <div className="flex flex-col justify-center gap-2">
                    <Label
                      htmlFor={`query-${id}`}
                      className="text-foreground text-xs"
                    >
                      Search Query
                    </Label>
                    <Input
                      id={`query-${id}`}
                      placeholder="Enter search query..."
                      className="h-8 text-xs"
                      value={(data.searchQuery as string) || ''}
                      onChange={(e) =>
                        handleInputChange('searchQuery', e.target.value)
                      }
                    />
                  </div>
                )}

                {data.toolName === 'web-scraping' && (
                  <div className="space-y-3">
                    <div className="flex flex-col justify-center gap-2">
                      <Label
                        htmlFor={`url-${id}`}
                        className="text-foreground text-xs"
                      >
                        Target URL
                      </Label>
                      <Input
                        id={`url-${id}`}
                        placeholder="https://example.com"
                        className="h-8 text-xs"
                        value={(data.targetUrl as string) || ''}
                        onChange={(e) =>
                          handleInputChange('targetUrl', e.target.value)
                        }
                      />
                    </div>

                    <div className="flex flex-col justify-center gap-2">
                      <Label
                        htmlFor={`css-selector-${id}`}
                        className="text-foreground text-xs"
                      >
                        CSS Selector (Optional)
                      </Label>
                      <Input
                        id={`css-selector-${id}`}
                        placeholder="body, .content, #main"
                        className="h-8 text-xs"
                        value={(data.cssSelector as string) || ''}
                        onChange={(e) =>
                          handleInputChange('cssSelector', e.target.value)
                        }
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`remove-images-${id}`}
                        checked={data.removeImages === 'true'}
                        onChange={(e) =>
                          handleInputChange(
                            'removeImages',
                            e.target.checked.toString()
                          )
                        }
                        className="h-3 w-3"
                      />
                      <Label
                        htmlFor={`remove-images-${id}`}
                        className="text-foreground text-xs"
                      >
                        Remove Images
                      </Label>
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`gather-links-${id}`}
                        checked={data.gatherLinks === 'true'}
                        onChange={(e) =>
                          handleInputChange(
                            'gatherLinks',
                            e.target.checked.toString()
                          )
                        }
                        className="h-3 w-3"
                      />
                      <Label
                        htmlFor={`gather-links-${id}`}
                        className="text-foreground text-xs"
                      >
                        Gather All Links
                      </Label>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        )}

        {/* Execution Status */}
        {isExecuting && (
          <div className="p-3">
            <div className="flex items-center gap-2 text-cyan-600 text-sm">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-cyan-600 border-t-transparent" />
              Executing {selectedTool?.label}...
            </div>
          </div>
        )}

        {/* Result Display */}
        {data.generated?.result && (
          <div className="space-y-3 p-3">
            <Label className="text-foreground text-xs">Result</Label>
            <div className="nowheel mt-1 h-48 max-h-48 overflow-y-auto rounded-xl bg-neutral-50 p-2 text-xs dark:bg-neutral-800">
              <ReactMarkdown>{data.generated.result}</ReactMarkdown>
            </div>
          </div>
        )}

        {/* Error Display */}
        {data.generated?.error && (
          <div className="p-3">
            <Label className="text-red-600 text-xs">Error</Label>
            <div className="mt-1 rounded border border-red-200 bg-red-50 p-2 text-xs dark:border-red-800 dark:bg-red-950">
              {data.generated.error}
            </div>
          </div>
        )}
      </div>
    </NodeLayout>
  );
}
